import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import TourFlight from "@/components/tour-detail/ModalRequestBookingTour/TourFlight"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useAirlines } from "@/hooks/flight/useAirlines"
import { useAppSelector } from "@/lib/redux/hooks"

import { parseJson } from "@/utils/safeParse/parseJson"

import { Col, Divider, Image, Row, theme } from "antd"
import { JSX, useMemo } from "react"
import { useTranslation } from "react-i18next"

const TourBooking = () => {
  const isDesktop = useIsDesktop()
  const orderDetail = useAppSelector(state => state.booking.order_detail.data)
  const token = theme.useToken().token
  const totalAmount = parseFloat(orderDetail?.totalAmount || "0")
  const discountAmount = parseFloat(orderDetail?.discountAmount || "0")
  const { input: contact, tour } = parseJson(orderDetail?.itinData) || { input: {} }
  const { passengers_adult, passengers_child1, passengers_child2, passengers_infant } = parseJson(orderDetail?.metaData) || { input: {} }
  const airlines = useAirlines()
  const departureSchedule = tour.departureSchedule[0]
  const { getFormatCurrency, getValidDayjs } = useTETranslation()
  const { t } = useTranslation()

  const renderInforPassenger = (passenger?: any): JSX.Element => {
    const birthday = getValidDayjs(passenger?.birthday)

    return (
      <>
        <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
          <Col xs={24} sm={{ flex: "20rem" }}>
            <Text>{t("common.fullname")}:</Text>
          </Col>
          <Col>
            {`(${passenger?.gender ? t("common.mr") : t("common.mrs")}) ${passenger?.fullName}`}
          </Col>
        </Row>
        <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
          <Col xs={24} sm={{ flex: "20rem" }}>
            <Text>{t("common.birthday")}:</Text>
          </Col>
          <Col>
            {passenger?.birthday ? birthday.format("DD/MM/YYYY") : `--/--/--`}
          </Col>
        </Row>
      </>
    )
  }

  const flights = useMemo(() => {
    return [departureSchedule?.ticketPlaneGo, departureSchedule?.ticketPlaneBack]
      .filter(str => !!str)
      .map(str =>
        str?.split(" // ").map(
          (str: string, idx: any) => {
            const [flightNumber, startPoint, endPoint, startTime, , endTime] = str?.split(" ") || []
            return {
              flightNumber,
              startPoint: startPoint,
              endPoint: endPoint,
              startTime,
              endTime,
              airline: airlines[flightNumber.slice(0, 2)],
              date: idx === 0
                ? getValidDayjs(departureSchedule?.startDate).format("DD-MM-YYYY")
                : getValidDayjs(departureSchedule?.endDate).format("DD-MM-YYYY")
            }
          }
        )
      )
  }, [departureSchedule])

  return (
    <>
      <Row>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("booking.contact_info")}
        </Title>
      </Row>
      <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
        <Col xs={24} sm={{ flex: "20rem" }}>
          <Text>{t("common.fullname")}:</Text>
        </Col>
        <Col>
          {`${contact?.fullName}`}
        </Col>
      </Row>
      <Row align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem' }}>
        <Col xs={24} sm={{ flex: "20rem" }}>
          <Text>{t("common.phonenumber")}: </Text>
        </Col>
        <Col>
          {`${contact?.phoneNumber}`}
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Divider style={{ marginBlock: '1.5rem' }} />
          <Row>
            <Title level={4} style={{ marginTop: 0 }}>
              {t("booking.payment_info")}
            </Title>
          </Row>

          <Row justify='space-between' align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem', maxWidth: '40rem' }}>
            <Col sm={{ flex: "20rem" }}>
              <Text>{t("common.discount")}: </Text>
            </Col>
            <Col>
              {getFormatCurrency(discountAmount)}
            </Col>
          </Row>

          <Row justify='space-between' align='middle' gutter={[15, 0]} style={{ marginBottom: isDesktop ? 0 : '1rem', maxWidth: '40rem' }}>
            <Col sm={{ flex: "20rem" }}>
              <Text strong>{t("common.total_amount")}: </Text>
            </Col>
            <Col>
              <Text type='danger' strong>{getFormatCurrency(totalAmount)}</Text>
            </Col>
          </Row>

        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Divider style={{ marginBlock: '1.5rem' }} />
          <Row>
            <Title level={4} style={{ marginTop: 0 }}>
              {t("booking.pass_info")}
            </Title>
          </Row>
          <Row gutter={[15, 15]}>
            {passengers_adult
              .filter((pass: any) => !!pass)
              .map((pass: any, index: number) => {
                return (
                  <Col xs={24} lg={12} xl={12}>
                    <Row>
                      <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                        {`${t("common.adult")} ${index + 1}`}
                      </Title>
                    </Row>
                    {renderInforPassenger(pass)}
                  </Col>
                )
              })}
            {(passengers_child1 || [])
              .filter((pass: any) => !!pass)
              .map((pass: any, index: number) => {
                return (
                  <Col xs={24} lg={12} xl={12}>
                    <Row>
                      <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                        {`${t("common.child")} 1 - ${index + contact.inputAdt + 1}`}
                      </Title>
                    </Row>
                    {renderInforPassenger(pass)}
                  </Col>
                )
              })}
            {(passengers_child2 || [])
              .filter((pass: any) => !!pass)
              .map((pass: any, index: number) => {
                return (
                  <Col xs={24} lg={12} xl={12}>
                    <Row>
                      <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                        {`${t("common.child")} 2 - ${index + (contact.inputChd1 || 0) + (contact.inputAdt || 0) + 1}`}
                      </Title>
                    </Row>
                    {renderInforPassenger(pass)}
                  </Col>
                )
              })}
            {(passengers_infant || [])
            .filter((pass: any) => !!pass)
            .map((pass: any, index: number) => {
              return (
                <Col xs={24} lg={12} xl={12}>
                  <Row>
                    <Title level={5} style={{ marginTop: 0, color: '#475467' }}>
                      {`${t("common.infant")} - ${index + (contact.inputChd1 || 0) + (contact.inputChd2 || 0) + (contact.inputAdt || 0) + 1}`}
                    </Title>
                  </Row>
                  {renderInforPassenger(pass)}
                </Col>
              )
            })}
          </Row>
        </Col>
      </Row>

      <Row className="booking__result--flights">
        <Col span={24}>
          <Divider style={{ marginBlock: '1.5rem' }} />
          <Row>
            <Title level={4} style={{ marginTop: 0 }}>
              {t("tour.tour_info")}
            </Title>
          </Row>

          <Row gutter={[15, 10]} justify='start'>
            <Col flex={"25rem"}>
              <Image
                preview={false}
                style={{ borderRadius: 8 }}
                src={(tour.images || [])[0]?.path}
              />
            </Col>
            <Col flex={1}>
              <Row>
                <Title style={{ margin: 0 }} level={4}>
                  {tour?.name}
                </Title>
              </Row>
              <Row>
                <Col span={isDesktop ? undefined : 24}>
                  <Row>
                    <Text style={{ marginRight: 10 }}>{t("tour.departure_date")}:</Text>{' '}<Text strong>{getValidDayjs(departureSchedule?.startDate).format("DD-MM-YYYY")}</Text>
                  </Row>
                  <Row>
                    <Text style={{ marginRight: 10 }}>{t("tour.tour_code")}:</Text> <Text strong ellipsis style={{ maxWidth: "12rem", width: "100%" }} copyable>{' '}{departureSchedule.tourId}</Text>
                  </Row>
                </Col>
                {isDesktop && (<Divider orientation='vertical' style={{ height: '12rem' }} />)}
                <Col span={isDesktop ? undefined : 24}>
                  <Row>
                    <Text style={{ marginRight: 10 }}>{t("tour.number_of_slot")}:</Text><Text strong>{contact.inputAdt + (contact.inputChd1 || 0) + (contact.inputChd2 || 0) + contact.inputInf}</Text>
                  </Row>
                  <Row>
                    <Text style={{ marginRight: 10 }}>{t("common.adult")}:</Text><Text strong>{contact.inputAdt}</Text>
                  </Row>
                  <Row>
                    <Text style={{ marginRight: 10 }}>{t("common.child")} 1:</Text><Text strong>{contact.inputChd1}</Text>
                  </Row>
                  {(!!tour.agesChildren2From && !!tour.agesChildren2To) && (
                    <Row>
                      <Text style={{ marginRight: 10 }}>{t("common.child")} 2:</Text><Text strong>{contact.inputChd2}</Text>
                    </Row>
                  )}
                  <Row>
                    <Text style={{ marginRight: 10 }}>{t("common.infant")}:</Text><Text strong>{contact.inputInf}</Text>
                  </Row>
                </Col>
              </Row>
              <Divider style={{ marginBlock: '1rem' }} />
              <Row justify='start'>
                <Title level={4} style={{ marginTop: 0 }}>
                  {t("tour.flight_info")}
                </Title>
              </Row>
              <Row gutter={15}>
                {flights.map((segments, idx) => {
                  return (
                    <Col xs={24} md={12} style={{ maxWidth: "25rem" }}>
                      <TourFlight segments={segments} index={idx} />
                    </Col>
                  )
                })}
              </Row>
            </Col>
          </Row>

        </Col>
      </Row>
    </>
  )
}

export default TourBooking